import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";
import AutoStackStep from "../../components/AutoStackStep";
import AutoWalletStep from "../../components/AutoWalletStep";
import ConfirmAutoStackStep from "../../components/ConfirmAutoStackStep";
import AutoStackStatusStep from "../../components/AutoStackStatusStep";
import SetUpRecurringPayment from "../../components/SetUpRecurringPayment";
import BankTransferSetup from "../../components/BankTransferSetup";
import {
  DEFAULT_FORM_VALUES,
  VALIDATION_MESSAGES,
} from "../../constants/autostack";
import { create } from "../../services/apiService";
import { useAssetsStore } from "../../store/use-assets-store";
import { AutoStackingTemplateResponse } from "../../types/generated/api";
import { useQueryDigitalWallet } from "../../hooks/query/queries";
import { Asset } from "../../types/asset";
import { useBankReferenceStore } from "../../store/use-bank-reference";
import { localTimeToUTC } from "../../utils/dateTime";

export const fundingTypeMap = {
  wallet: "wallet",
  bank: "bank_transfer",
} as const;

const createAutoStackFormSchema = (portfolioEstimate: number | null) =>
  z
    .object({
      stackName: z
        .string()
        .transform((val) => val.trim())
        .refine((val) => val.length > 0, {
          message: VALIDATION_MESSAGES.REQUIRED_STACK_NAME,
        })
        .refine((val) => val.length <= 50, {
          message: VALIDATION_MESSAGES.STACK_NAME_MAX_LENGTH,
        }),
      stackType: z.enum(["wallet", "bank"]),

      stackAmount: z
        .string()
        .min(1, VALIDATION_MESSAGES.REQUIRED_AMOUNT)
        .refine((val) => {
          const num = Number(val.replace(/,/g, ""));
          return !isNaN(num) && num > 0;
        }, VALIDATION_MESSAGES.INVALID_AMOUNT_MIN),
      assetToBuy: z.string().min(1, VALIDATION_MESSAGES.REQUIRED_ASSET),
      frequency: z
        .object({
          type: z.enum(["day", "week", "month"]),
          dayOfWeek: z.string().optional(),
          dayOfMonth: z.string().optional(),
          time: z.string().optional(),
        })
        .refine((data) => data.type, VALIDATION_MESSAGES.REQUIRED_FREQUENCY)
        .refine((data) => {
          if (data.type === "day" && !data.time) {
            return false;
          }
          return true;
        }, VALIDATION_MESSAGES.REQUIRED_TIME),
      startDateTime: z.string().min(1, VALIDATION_MESSAGES.REQUIRED_START_DATE),
    })
    .refine(
      (data) => {
        if (data.stackType === "wallet" && portfolioEstimate !== null) {
          const stackAmount = Number(data.stackAmount.replace(/,/g, ""));
          return stackAmount <= portfolioEstimate;
        }
        return true;
      },
      {
        message: VALIDATION_MESSAGES.INSUFFICIENT_BALANCE,
        path: ["stackAmount"],
      }
    );

export type AutoStackFormData = z.infer<
  ReturnType<typeof createAutoStackFormSchema>
>;
export type AutoStackStepType =
  | "stack-setup"
  | "wallet-config"
  | "recurring-payment"
  | "bank-transfer-setup"
  | "confirm"
  | "status";

const transformTemplateToFormData = (
  template: AutoStackingTemplateResponse & { asset: Asset }
): Partial<AutoStackFormData> => {
  const frequencyMap = {
    daily: "day",
    weekly: "week",
    monthly: "month",
  } as const;

  const formFrequencyType =
    frequencyMap[template.frequency as keyof typeof frequencyMap] || "week";

  let frequencyObj: AutoStackFormData["frequency"] = {
    type: formFrequencyType,
  };

  if (template.schedule) {
    if (
      template.schedule.dayOfWeek &&
      typeof template.schedule.dayOfWeek === "string"
    ) {
      frequencyObj.dayOfWeek = template.schedule.dayOfWeek;
    }
    if (template.schedule.time && typeof template.schedule.time === "string") {
      frequencyObj.time = template.schedule.time;
    }
    if (
      template.schedule.monthlyType &&
      typeof template.schedule.monthlyType === "string"
    ) {
      if (
        template.schedule.monthlyType === "custom" &&
        template.schedule.customDay &&
        typeof template.schedule.customDay === "number"
      ) {
        frequencyObj.dayOfMonth = template.schedule.customDay.toString();
      } else {
        frequencyObj.dayOfMonth = template.schedule.monthlyType;
      }
    }
  }

  return {
    stackName: template.name || "",
    assetToBuy: template.asset?.asset || "",
    stackAmount: template.amount?.toString() || "",
    frequency: frequencyObj,
    stackType: "wallet",
    startDateTime: "",
  };
};

const transformFormDataToApi = (
  formData: AutoStackFormData,
  assets: Asset[],
  generateReference: (type: string) => string | null
) => {
  const {
    stackName,
    assetToBuy,
    stackAmount,
    frequency,
    stackType,
    startDateTime,
  } = formData;

  const frequencyMap = {
    day: "daily",
    week: "weekly",
    month: "monthly",
  } as const;

  const apiFrequency = frequencyMap[frequency.type];
  const apiFundingType =
    fundingTypeMap[stackType as keyof typeof fundingTypeMap];

  let schedule: {
    time?: string;
    dayOfWeek?: string;
    monthlyType?: string;
    customDay?: number;
  } = {};
  const extractTimeFromDateTime = (dateTimeStr: string): string => {
    if (!dateTimeStr) return "08:00";
    const parts = dateTimeStr.split(", ");
    if (parts.length >= 2) {
      const timePart = parts[parts.length - 1];
      if (/^\d{2}:\d{2}$/.test(timePart)) {
        return timePart;
      }
    }
    return "08:00";
  };

  const selectedTime = frequency.time ?? extractTimeFromDateTime(startDateTime);

  if (frequency.type === "day") {
    schedule = {
      time: localTimeToUTC(selectedTime),
    };
  } else if (frequency.type === "week") {
    schedule = {
      dayOfWeek: (frequency.dayOfWeek ?? "tuesday").toLowerCase(),
      time: localTimeToUTC(selectedTime),
    };
  } else if (frequency.type === "month") {
    if (frequency.dayOfMonth === "first" || frequency.dayOfMonth === "last") {
      schedule = {
        monthlyType: frequency.dayOfMonth,
        time: localTimeToUTC(selectedTime),
      };
    } else if (frequency.dayOfMonth && !isNaN(parseInt(frequency.dayOfMonth))) {
      schedule = {
        monthlyType: "custom",
        customDay: parseInt(frequency.dayOfMonth),
        time: localTimeToUTC(selectedTime),
      };
    } else {
      schedule = {
        monthlyType: "first",
        time: localTimeToUTC(selectedTime),
      };
    }
  }

  const assetID = assets.find((asset) => asset.asset === assetToBuy)?._id;

  const parseStartDateTime = (dateTimeStr: string): string | undefined => {
    if (!dateTimeStr) return undefined;
    const parts = dateTimeStr.split(", ");
    if (parts.length < 2) return undefined;

    const datePart = parts[0];
    const timePart = parts[1];

    const parsedDate = new Date(`${datePart} ${timePart}`);
    if (isNaN(parsedDate.getTime())) return undefined;

    return parsedDate.toISOString();
  };

  return {
    asset: assetID,
    amount: parseFloat(stackAmount) || 0,
    frequency: apiFrequency,
    schedule,
    name: stackName,
    funding_type: apiFundingType,
    ...(startDateTime && { start_date: parseStartDateTime(startDateTime) }),
    ...(apiFundingType === "bank_transfer" && {
      stackBankReference: generateReference(assetToBuy),
    }),
  };
};

const AutoStack: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] =
    useState<AutoStackStepType>("stack-setup");
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  const { fetchAssets, assets } = useAssetsStore();
  const { resetBankReference, fetchBankReference, generateReference } =
    useBankReferenceStore();
  const { digitalWalletData } = useQueryDigitalWallet();

  const fiatBalance =
    digitalWalletData?.balances?.assetBalances?.fiat?.[0]?.availableAmount ||
    null;

  const prefillData = location.state?.prefillData as
    | AutoStackingTemplateResponse
    | undefined;

  const formDefaultValues = prefillData
    ? {
        ...DEFAULT_FORM_VALUES,
        ...transformTemplateToFormData(
          prefillData as AutoStackingTemplateResponse & { asset: Asset }
        ),
      }
    : DEFAULT_FORM_VALUES;

  const form = useForm<AutoStackFormData>({
    resolver: zodResolver(createAutoStackFormSchema(fiatBalance)),
    defaultValues: formDefaultValues,
    mode: "onSubmit",
  });

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const getFieldsForStep = useCallback(
    (step: AutoStackStepType): (keyof AutoStackFormData)[] => {
      switch (step) {
        case "stack-setup":
          return ["stackName", "stackType"];
        case "wallet-config":
          return ["stackAmount", "assetToBuy", "frequency", "startDateTime"];
        case "recurring-payment":
          return [];
        case "confirm":
          return [];
        default:
          return [];
      }
    },
    []
  );

  const handleFinalSubmit = useCallback(async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    try {
      const formData = form.getValues();
      const apiData = transformFormDataToApi(
        formData,
        assets,
        generateReference
      );

      const response = await create(
        "/api/v1/auto-stacking/auto-stacks",
        apiData
      );

      if (response.status === 200 || response.status === 201) {
        setIsSuccess(true);
        setCurrentStep("status");
        resetBankReference();
        await fetchBankReference();
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["userAutoStacks"] }),
          queryClient.invalidateQueries({ queryKey: ["autoStackStats"] }),
          queryClient.invalidateQueries({ queryKey: ["currencies"] }),
          queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        ]);
      } else {
        throw new Error("Failed to create AutoStack template");
      }
    } catch (error) {
      console.error("Error creating AutoStack template:", error);
      setIsSuccess(false);
      setCurrentStep("status");
    }
  }, [
    form,
    assets,
    generateReference,
    resetBankReference,
    fetchBankReference,
    queryClient,
  ]);

  const handleStepComplete = useCallback(async () => {
    const isValid = await form.trigger(getFieldsForStep(currentStep));

    if (!isValid) {
      return;
    }

    if (currentStep === "stack-setup") {
      setCurrentStep("wallet-config");
    } else if (currentStep === "wallet-config") {
      const stackType = form.getValues("stackType");
      if (stackType === "bank") {
        setCurrentStep("recurring-payment");
      } else {
        setCurrentStep("confirm");
      }
    } else if (currentStep === "recurring-payment") {
      setCurrentStep("bank-transfer-setup");
    } else if (currentStep === "bank-transfer-setup") {
      setCurrentStep("confirm");
    } else if (currentStep === "confirm") {
      await handleFinalSubmit();
    }
  }, [currentStep, form, getFieldsForStep, handleFinalSubmit]);

  const handleStepBack = useCallback(() => {
    if (currentStep === "status") {
      setCurrentStep("confirm");
    } else if (currentStep === "confirm") {
      const stackType = form.getValues("stackType");
      if (stackType === "bank") {
        setCurrentStep("bank-transfer-setup");
      } else {
        setCurrentStep("wallet-config");
      }
    } else if (currentStep === "bank-transfer-setup") {
      setCurrentStep("recurring-payment");
    } else if (currentStep === "recurring-payment") {
      setCurrentStep("wallet-config");
    } else if (currentStep === "wallet-config") {
      setCurrentStep("stack-setup");
    } else if (currentStep === "stack-setup") {
      navigate("/autostack");
    }
  }, [currentStep, navigate, form]);

  const handleCancel = useCallback(() => {
    navigate("/autostack");
  }, [navigate]);

  const handleStatusContinue = useCallback(() => {
    if (isSuccess) {
      navigate("/autostack");
    } else {
      setCurrentStep("confirm");
    }
  }, [isSuccess, navigate]);

  const renderCurrentStep = useCallback(() => {
    const commonProps = {
      onStepComplete: handleStepComplete,
      onStepBack: handleStepBack,
      onCancel: handleCancel,
      currentStep,
    };

    switch (currentStep) {
      case "stack-setup":
        return <AutoStackStep {...commonProps} />;

      case "wallet-config":
        return <AutoWalletStep {...commonProps} />;

      case "recurring-payment":
        return (
          <SetUpRecurringPayment
            onContinue={handleStepComplete}
            onStepBack={handleStepBack}
          />
        );

      case "bank-transfer-setup":
        return (
          <BankTransferSetup
            onContinue={handleStepComplete}
            onStepBack={handleStepBack}
            onCancel={handleCancel}
          />
        );

      case "confirm":
        return <ConfirmAutoStackStep {...commonProps} />;

      case "status":
        return (
          <AutoStackStatusStep
            isSuccess={isSuccess}
            onContinue={handleStatusContinue}
          />
        );

      default:
        return null;
    }
  }, [
    currentStep,
    handleStepComplete,
    handleStepBack,
    handleCancel,
    isSuccess,
    handleStatusContinue,
  ]);

  return <FormProvider {...form}>{renderCurrentStep()}</FormProvider>;
};

export default AutoStack;
